import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Button, Grid, TextField, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { GlobalContext } from "../../contexts/GlobalContext"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import Swal from "sweetalert2"
import dayjs from "dayjs"
import getIconComponent from "../utils/getIconComponent"
import { handleAllExportDataCSV } from "../utils/reactTableActions/exportToolbarActions"

// Importaciones para generación del PDF estructurado
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"

// Íconos
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const ConsTrazaSeriesIndex = () => {
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual
  const [expanded, setExpanded] = useState(true)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado de los filtros
  const [filtros, setFiltros] = useState({
    sernumero: "",
    artcodigo: "",
    artdescri: "",
  })

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    if (!filtros.sernumero.trim() && !filtros.artcodigo.trim() && !filtros.artdescri.trim()) {
      Swal.fire({
        title: "Aviso",
        text: "Ingrese al menos un criterio de búsqueda (Serie, Artículo o Descripción).",
        icon: "warning",
        confirmButtonColor: "#196C87",
      })
      return
    }

    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  // Helper para traer todos los datos de exportación
  const fetchAllDataForExport = async (columnsFilters) => {
    try {
      Swal.fire({
        title: "Generando Archivo...",
        text: "Extrayendo la totalidad de los registros de la base de datos.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })

      const dictFilters = {}
      columnsFilters.forEach((f) => {
        dictFilters[f.id] = f.value
      })

      const payload = {
        page: 1,
        perPage: 999999, // Solicitar el lote completo
        filters: dictFilters,
        externalFilters: filtros,
      }

      const response = await fetchwrapper("/ConsTrazaSeries/getAllTrazabilidadSeries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      const result = await response.json()
      let allRows = []

      if (result?.data && Array.isArray(result.data)) {
        allRows = result.data
      } else if (result?.data?.data && Array.isArray(result.data.data)) {
        allRows = result.data.data
      } else if (Array.isArray(result)) {
        allRows = result
      }

      if (allRows.length === 0) {
        Swal.fire("Aviso", "No hay datos para exportar.", "info")
        return null
      }

      Swal.close()
      return allRows
    } catch (error) {
      console.error("Error obteniendo datos para exportar:", error)
      Swal.fire("Error", "No se pudo generar la exportación.", "error")
      return null
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Trazabilidad de Series y Equipos</b>
        </div>

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Criterios de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Número de Serie"
                    size="small"
                    value={filtros.sernumero}
                    onChange={(e) => handleInputChange("sernumero", e.target.value.toUpperCase())}
                    placeholder="Ej. S/N, IMEI, Chasis..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Código Artículo"
                    size="small"
                    value={filtros.artcodigo}
                    onChange={(e) => handleInputChange("artcodigo", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth
                    label="Descripción del Artículo"
                    size="small"
                    value={filtros.artdescri}
                    onChange={(e) => handleInputChange("artdescri", e.target.value.toUpperCase())}
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Consultar
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS TRANSSACCIONAL */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            endpoint={refreshTableKey === 0 ? "" : "/ConsTrazaSeries/getAllTrazabilidadSeries"}
            endpointJson={{
              filters: {},
              externalFilters: filtros,
            }}
            errorMsgFilterSearch="Error al cargar la trazabilidad de series."
            queryKeyModal="ConsTrazaSeriesData"
            perPage={15}
            rowActions={() => []}
            rowActionsWidthTable={0}
            topToolbarCustomActions={({ table }) => {
              const acciones = selectedMenuInfo?.data?.barraAcciones || []
              const toolbarActions = []

              const exportarAction = acciones.find((a) => a.acccaption === "EXPORTAR")
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF (Todos)",
                      key: "pdf",
                      onClick: async ({ columns }) => {
                        const rawFilters = table.getState().columnFilters || []
                        const allData = await fetchAllDataForExport(rawFilters)

                        if (allData) {
                          try {
                            const nombreCia = localStorage.getItem("ciaalias") || "EMPRESA NO DEFINIDA"
                            const usuarioSesion = localStorage.getItem("cliciausu") || "USUARIO DESCONOCIDO"

                            // A2 en 'landscape' provee un lienzo inmenso ideal para +15 columnas
                            const doc = new JsPDF("landscape", "pt", "a2")

                            // --- ENCABEZADO INSTITUCIONAL ---
                            doc.setFontSize(18)
                            doc.setTextColor(25, 108, 135)
                            doc.text(nombreCia, 40, 40)

                            doc.setFontSize(14)
                            doc.setTextColor(0, 0, 0)
                            doc.text("REPORTE DE TRAZABILIDAD DE SERIES Y EQUIPOS", 40, 65)

                            doc.setFontSize(10)
                            doc.setTextColor(60, 60, 60)
                            doc.text(`Criterio Serie: ${filtros.sernumero || "Todos"}`, 40, 85)
                            doc.text(
                              `Criterio Artículo: ${filtros.artcodigo || "Todos"} / ${filtros.artdescri || "Todos"}`,
                              40,
                              100,
                            )
                            doc.text(`Fecha de Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 40, 115)

                            // Preparamos las cabeceras y los datos mapeando las columnas visibles
                            const tableHeaders = columns.map((c) => c.header)
                            const tableData = allData.map((row) =>
                              columns.map((col) => {
                                const val = row[col.accessorKey]
                                if (col.accessorKey === "trafecha" && val) return val.split(" ")[0]
                                return val !== null && val !== undefined ? String(val) : ""
                              }),
                            )

                            autoTable(doc, {
                              head: [tableHeaders],
                              body: tableData,
                              startY: 135,
                              theme: "plain",
                              styles: {
                                fontSize: 9,
                                cellPadding: 4,
                                textColor: [40, 40, 40],
                                overflow: "linebreak",
                              },
                              headStyles: {
                                fillColor: [25, 108, 135],
                                textColor: [255, 255, 255],
                                fontStyle: "bold",
                              },
                              alternateRowStyles: { fillColor: [250, 250, 250] },

                              // --- HOOK PARA IMPRIMIR USUARIO Y PÁGINA EN TODAS LAS HOJAS ---
                              didDrawPage: function (data) {
                                const pageSize = doc.internal.pageSize
                                const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
                                const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

                                doc.setFontSize(9)
                                doc.setTextColor(150, 150, 150)

                                // Usuario inferior izquierda
                                doc.text(`Generado por: ${usuarioSesion}`, data.settings.margin.left, pageHeight - 20)

                                // Página inferior derecha
                                const str = `Página ${doc.internal.getNumberOfPages()}`
                                doc.text(str, pageWidth - data.settings.margin.right, pageHeight - 20, {
                                  align: "right",
                                })
                              },
                            })

                            doc.save(`Trazabilidad_Series_${dayjs().format("DD-MM-YYYY_HHmm")}.pdf`)
                          } catch (error) {
                            console.error("Error generando PDF gigante:", error)
                            Swal.fire("Error", "Ocurrió un problema al generar el PDF estructurado.", "error")
                          }
                        }
                      },
                    },
                    {
                      label: "Exportar CSV (Todos)",
                      key: "csv",
                      onClick: async () => {
                        const rawFilters = table.getState().columnFilters || []
                        const allData = await fetchAllDataForExport(rawFilters)
                        if (allData) {
                          handleAllExportDataCSV(allData, `Trazabilidad_Series`)
                        }
                      },
                    },
                  ],
                })
              }

              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "trafecha",
                header: "Fecha",
                size: 110,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()?.split(" ")[0]}</span>,
              },
              { accessorKey: "trahora", header: "Hora", size: 100 },
              { accessorKey: "serusuisys", header: "Usuario", size: 130 },
              {
                accessorKey: "sernumero",
                header: "Serie",
                size: 160,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              { accessorKey: "artcodigo", header: "Artículo", size: 130 },
              { accessorKey: "artdescri", header: "Descripción Artículo", size: 250 },
              { accessorKey: "boddescri", header: "Bodega", size: 150 },
              { accessorKey: "sermotor", header: "Motor", size: 140 },
              { accessorKey: "sermodanio", header: "Año", size: 80 },
              { accessorKey: "paisdescri", header: "País", size: 130 },
              { accessorKey: "coldescri", header: "Color", size: 130 },
              { accessorKey: "tranumero", header: "Transacción", size: 130 },
              { accessorKey: "tradescri", header: "Desc. Transacción", size: 180 },
              { accessorKey: "tradescripcion", header: "Comentario", size: 250 },
              { accessorKey: "traorigen", header: "Origen Trans.", size: 120 },
              {
                accessorKey: "documento_mostrar",
                header: "Documento",
                size: 150,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
              },
              { accessorKey: "cliente_proveedor", header: "Cliente/Proveedor", size: 250 },
              {
                accessorKey: "serstatus_desc",
                header: "Estado",
                size: 120,
                Cell: ({ cell }) => {
                  const val = cell.getValue()
                  let color = "textPrimary"
                  if (val === "ACTIVA") color = "#2e7d32"
                  if (val === "FACTURADA") color = "#d32f2f"
                  if (val === "RESERVADA") color = "#ed6c02"
                  if (val === "TRANSFERIDA") color = "#1976d2"
                  if (val === "EGRESADA") color = "#9c27b0"

                  return <span style={{ fontWeight: "bold", color }}>{val}</span>
                },
              },
              { accessorKey: "pednumped", header: "Pedido", size: 120 },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ConsTrazaSeriesIndex
